import { Banknote, LockKeyhole, LogOut, Monitor, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Cart } from "@/components/Cart.tsx";
import { CloseCashDialog } from "@/components/CloseCashDialog.tsx";
import { PaymentDialog } from "@/components/PaymentDialog.tsx";
import { ProductSearch } from "@/components/ProductSearch.tsx";
import { formatPEN } from "@/lib/utils.ts";
import { useCurrentCashSession } from "@/queries/cashSessions.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useCartStore } from "@/stores/cart.ts";
import { useTerminalStore } from "@/stores/terminal.ts";

export function POSPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const terminal = useTerminalStore((s) => s.terminal);
  const unpairTerminal = useTerminalStore((s) => s.unpair);
  const addProduct = useCartStore((s) => s.addProduct);

  const currentSession = useCurrentCashSession();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [closeCashOpen, setCloseCashOpen] = useState(false);

  // Si no hay sesión de caja abierta, ir a apertura.
  useEffect(() => {
    if (currentSession.isSuccess && currentSession.data === null) {
      navigate("/open-caja", { replace: true });
    }
  }, [currentSession.isSuccess, currentSession.data, navigate]);

  if (currentSession.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        Cargando sesión…
      </div>
    );
  }

  const changeTerminal = () => {
    // Solo con la caja cerrada: la sesión abierta está atada a este terminal.
    if (currentSession.data) {
      alert(
        "Cierra la caja antes de cambiar de terminal. La sesión abierta está atada a esta caja.",
      );
      return;
    }
    unpairTerminal();
    navigate("/select-terminal", { replace: true });
  };

  const headerRight = (
    <>
      {currentSession.data && (
        <div className="rounded-xl bg-slate-50 px-3.5 py-1.5" title="Efectivo esperado en caja">
          <p className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
            <Banknote className="h-3.5 w-3.5" /> En caja
          </p>
          <p className="text-base font-extrabold tabular-nums text-slate-900">
            {formatPEN(currentSession.data.expected_cash)}
          </p>
        </div>
      )}
      {currentSession.data && (
        <button
          type="button"
          onClick={() => setCloseCashOpen(true)}
          className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <LockKeyhole className="h-4 w-4" /> Cerrar caja
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          clearSession();
          unpairTerminal();
          navigate("/login", { replace: true });
        }}
        aria-label="Salir"
        title="Salir"
        className="rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
      >
        <LogOut className="h-5 w-5" />
      </button>
    </>
  );

  const subtitle = terminal ? (
    <button
      type="button"
      onClick={changeTerminal}
      title={`Cambiar caja (actual: ${terminal.code})`}
      className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline"
    >
      <Monitor className="h-3 w-3" />
      {terminal.name ?? terminal.code}
      <RefreshCw className="h-3 w-3 opacity-60" />
    </button>
  ) : null;

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <ProductSearch
        onSelect={(p) => addProduct(p)}
        scanEnabled={!paymentOpen && !closeCashOpen}
        branchId={terminal?.branchId}
        subtitle={subtitle}
        headerRight={headerRight}
        aside={<Cart onCheckout={() => setPaymentOpen(true)} cashierName={user?.full_name} />}
      />

      {paymentOpen && currentSession.data && (
        <PaymentDialog
          branchId={currentSession.data.branch_id}
          onClose={() => setPaymentOpen(false)}
        />
      )}

      {closeCashOpen && currentSession.data && (
        <CloseCashDialog
          sessionId={currentSession.data.id}
          onClose={() => setCloseCashOpen(false)}
        />
      )}
    </div>
  );
}
